import sendClarityEvent from "utils/clarity"
import { sendHotjarEvent } from "utils/hotjar"
import recordSessionEventInSentry from "utils/sentry-replay"
import SESSION_RECORDING_EVENTS from "utils/session-recording-events"

export { SESSION_RECORDING_EVENTS }

// Hotjar stores event timestamps with millisecond precision and no sequence
// counter, so events emitted within the same tick cannot be ordered by its
// funnels. The queue below spaces emissions by EVENT_GAP_MS to keep every
// event on its own millisecond and tick.
//
// Right after snow_sub_popup_opened the Hotjar tracker may (re)establish its
// websocket (that event triggers session capture). Until the socket is
// connected, each newly written event overwrites the previous unsent one, so
// after that event the queue holds back for CAPTURE_START_HOLD_MS once.
const EVENT_GAP_MS = 50
const CAPTURE_START_HOLD_MS = 2500

// The Hotjar funnel steps. Each step matches any of its alternative events.
const FUNNEL_STEPS = [
  [SESSION_RECORDING_EVENTS.subPopupOpened],
  [
    SESSION_RECORDING_EVENTS.subPopupReading,
    SESSION_RECORDING_EVENTS.subPopupInteraction,
    SESSION_RECORDING_EVENTS.subPopupEngagementDummy,
  ],
  [SESSION_RECORDING_EVENTS.subConfirmationViewed],
  [SESSION_RECORDING_EVENTS.subCheckoutStarted],
  [SESSION_RECORDING_EVENTS.subPaymentSuccess],
]

// One representative event per step, used to backfill steps a user skipped
// (e.g. jumping straight to the confirmation screen, or returning from Stripe
// into a fresh Hotjar session) so the funnel never loses the session.
const STEP_BACKFILL_EVENTS = [
  SESSION_RECORDING_EVENTS.subPopupOpened,
  SESSION_RECORDING_EVENTS.subPopupEngagementDummy,
  SESSION_RECORDING_EVENTS.subConfirmationViewed,
  SESSION_RECORDING_EVENTS.subCheckoutStarted,
  SESSION_RECORDING_EVENTS.subPaymentSuccess,
]

const queue = []
const reachedSteps = new Set()
let notBefore = 0
let holdApplied = false
let timerId = null

function funnelStepIndex(eventName) {
  return FUNNEL_STEPS.findIndex((names) => names.includes(eventName))
}

function processQueue() {
  timerId = null
  if (!queue.length) return

  const now = Date.now()
  if (now < notBefore) {
    timerId = setTimeout(processQueue, notBefore - now)
    return
  }

  const eventName = queue.shift()
  sendHotjarEvent(eventName)
  sendClarityEvent(eventName)

  if (eventName === SESSION_RECORDING_EVENTS.subPopupOpened && !holdApplied) {
    holdApplied = true
    notBefore = now + CAPTURE_START_HOLD_MS
  } else {
    notBefore = now + EVENT_GAP_MS
  }

  if (queue.length) {
    timerId = setTimeout(processQueue, Math.max(notBefore - Date.now(), 0))
  }
}

function schedule(delay) {
  if (timerId !== null) return
  timerId = setTimeout(processQueue, Math.max(delay, 0))
}

function queueHotjarAndClarityEvent(eventName, step = funnelStepIndex(eventName)) {
  if (step !== -1) reachedSteps.add(step)
  queue.push(eventName)
  schedule(0)
}

export function sendSessionRecordingEvent(eventName) {
  const step = funnelStepIndex(eventName)
  // Sentry receives only observed events and needs no event spacing.
  recordSessionEventInSentry(eventName, step)
  queueHotjarAndClarityEvent(eventName, step)
}

// Queues a Hotjar/Clarity funnel-step event, backfilling any earlier steps
// that have not been reached on this page, so those sessions always carry an
// ordered event for every step of the funnel.
export function sendSubscriptionFunnelEvent(eventName) {
  const step = funnelStepIndex(eventName)
  for (let i = 0; i < step; i += 1) {
    if (!reachedSteps.has(i)) queueHotjarAndClarityEvent(STEP_BACKFILL_EVENTS[i], i)
  }
  sendSessionRecordingEvent(eventName)
}

// Drops the remaining hold so pending events drain before the page unloads
// (called when the Stripe Checkout redirect is imminent).
export function flushSessionRecordingEvents() {
  notBefore = 0
  if (timerId !== null) {
    clearTimeout(timerId)
    timerId = null
  }
  schedule(0)
}
